import { useEffect, useRef, useCallback, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

// Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s cap. Retries indefinitely.
const BACKOFF_MS = (attempt) => Math.min(1000 * Math.pow(2, attempt), 30000);
const HANDSHAKE_TIMEOUT_MS = 10000;

/**
 * Singleton-per-user WebSocket connection store.
 *
 * Why: multiple components (GroupChat, GlobalConnectionStatus, NotificationBell)
 * all want the same WS. If each opened its own socket, the backend
 * ConnectionManager keeps only the latest (user_id -> socket) entry — older
 * sockets would silently stop receiving server-pushed events.
 *
 * Solution: a small module-level cache that holds at most one socket per
 * userId, plus a fan-out listener set so every useWebSocket() consumer for that
 * userId observes the same state and receives every inbound message.
 */
const _stores = new Map(); // userId -> Store

function _getStore(userId) {
  let store = _stores.get(userId);
  if (store) return store;

  const state = {
    isConnected: false,
    reconnectAttempts: 0,
    ws: null,
    handshakeTimer: null,
    pingTimer: null,
    backoffTimer: null,
    attempts: 0,
    listeners: new Set(), // state listeners (for useSyncExternalStore)
    messageListeners: new Set(), // (data) => void
    refCount: 0,
    closed: false, // true once nobody is using us; prevents reconnect loops
  };

  const emit = () => {
    for (const fn of state.listeners) fn();
  };

  const cleanup = () => {
    if (state.handshakeTimer) {
      clearTimeout(state.handshakeTimer);
      state.handshakeTimer = null;
    }
    if (state.pingTimer) {
      clearInterval(state.pingTimer);
      state.pingTimer = null;
    }
  };

  const connect = () => {
    if (state.closed) return;
    let ws;
    try {
      ws = new WebSocket(`${WS_URL}/api/ws/${userId}`);
    } catch (err) {
      console.error('WebSocket construction failed:', err);
      scheduleReconnect();
      return;
    }
    state.ws = ws;

    state.handshakeTimer = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        console.warn('WebSocket handshake timeout — forcing close & retry');
        try {
          ws.close();
        } catch (err) {
          // ws.close() can throw on already-closed sockets — harmless,
          // but log for visibility per code-review (iter-51).
          console.debug('[ws] close-during-handshake-timeout failed:', err?.message);
        }
      }
    }, HANDSHAKE_TIMEOUT_MS);

    ws.onopen = () => {
      if (state.handshakeTimer) {
        clearTimeout(state.handshakeTimer);
        state.handshakeTimer = null;
      }
      state.isConnected = true;
      state.attempts = 0;
      state.reconnectAttempts = 0;
      emit();
      state.pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pong') return;
        for (const fn of state.messageListeners) {
          try {
            fn(data);
          } catch (e) {
            console.error('WS message listener failed:', e);
          }
        }
      } catch (err) {
        console.error('WebSocket message parse error:', err);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onclose = (event) => {
      cleanup();
      state.isConnected = false;
      emit();
      if (state.closed || event.code === 1000) return;
      scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (state.closed) return;
    if (state.backoffTimer) return; // already scheduled
    const delay = BACKOFF_MS(state.attempts);
    state.attempts += 1;
    state.reconnectAttempts = state.attempts;
    emit();
    state.backoffTimer = setTimeout(() => {
      state.backoffTimer = null;
      connect();
    }, delay);
  };

  const send = (message) => {
    if (state.ws?.readyState === WebSocket.OPEN) {
      state.ws.send(JSON.stringify(message));
      return true;
    }
    return false;
  };

  const forceReconnect = () => {
    state.attempts = 0;
    state.reconnectAttempts = 0;
    emit();
    if (state.ws && state.ws.readyState <= WebSocket.OPEN) {
      try {
        state.ws.close();
      } catch (err) {
        console.debug('[ws] explicit-close failed:', err?.message);
      }
    } else {
      if (state.backoffTimer) {
        clearTimeout(state.backoffTimer);
        state.backoffTimer = null;
      }
      connect();
    }
  };

  const subscribeState = (listener) => {
    state.listeners.add(listener);
    return () => state.listeners.delete(listener);
  };

  const subscribeMessages = (listener) => {
    if (!listener) return () => {};
    state.messageListeners.add(listener);
    return () => state.messageListeners.delete(listener);
  };

  const acquire = () => {
    state.refCount += 1;
    if (state.refCount === 1 && !state.ws) {
      state.closed = false;
      connect();
    }
  };

  const release = () => {
    state.refCount -= 1;
    if (state.refCount <= 0) {
      state.refCount = 0;
      state.closed = true;
      if (state.backoffTimer) {
        clearTimeout(state.backoffTimer);
        state.backoffTimer = null;
      }
      cleanup();
      if (state.ws) {
        try {
          state.ws.close(1000, 'no subscribers');
        } catch (err) {
          console.debug('[ws] last-subscriber-close failed:', err?.message);
        }
        state.ws = null;
      }
      _stores.delete(userId);
    }
  };

  store = {
    getSnapshot: () => state,
    subscribeState,
    subscribeMessages,
    send,
    forceReconnect,
    acquire,
    release,
  };
  _stores.set(userId, store);
  return store;
}

/**
 * useWebSocket — singleton-per-user WS hook.
 *
 * Multiple components can call useWebSocket(userId) simultaneously: they share
 * one socket, observe the same connection state, and each receives every
 * inbound message via their own onMessage callback.
 *
 * @param {string} userId   — required; passing null/undefined returns inert state.
 * @param {(msg) => void} [onMessage] — optional inbound message handler.
 */
export const useWebSocket = (userId, onMessage) => {
  const onMessageRef = useRef(onMessage);
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Build a snapshot so React tracks isConnected + reconnectAttempts reactively.
  const store = userId ? _getStore(userId) : null;
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!store) return undefined;
    store.acquire();
    const unsubState = store.subscribeState(() => forceRender((n) => n + 1));
    const unsubMsg = store.subscribeMessages((data) => onMessageRef.current?.(data));
    return () => {
      unsubState();
      unsubMsg();
      store.release();
    };
  }, [store]);

  const snapshot = store?.getSnapshot();
  const sendMessage = useCallback((m) => (store ? store.send(m) : false), [store]);
  const reconnect = useCallback(() => store?.forceReconnect(), [store]);

  return {
    isConnected: !!snapshot?.isConnected,
    reconnectAttempts: snapshot?.reconnectAttempts ?? 0,
    sendMessage,
    reconnect,
  };
};

// Message type constants
export const WSMessageType = {
  GROUP_MESSAGE: 'group_message',
  GROUP_JOIN: 'group_join',
  GROUP_LEAVE: 'group_leave',
  GROUP_TYPING: 'group_typing',
  DIRECT_MESSAGE: 'direct_message',
  MESSAGE_READ: 'message_read',
  NOTIFICATION: 'notification',
  CONNECTION_REQUEST: 'connection_request',
  CONNECTION_ACCEPTED: 'connection_accepted',
  NEW_FOLLOWER: 'new_follower',
  USER_ONLINE: 'user_online',
  USER_OFFLINE: 'user_offline',
  STATUS_UPDATE: 'status_update',
  EVENT_RSVP: 'event_rsvp',
  NEW_EVENT: 'new_event',
};

export default useWebSocket;
